import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgButton, IgDialog, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListDto,
  GenericExperienceFe,
  OrganizationDto,
  ScheduleDto,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { getDialog, setDialog } from "@src/store/slices/global";
import { fullDateTimeConverter } from "@src/utils/dateConverters";

interface ActiveScheduleDialogProps {
  handleEnableToggle: (personalization: ExperienceListDto) => void;
  organization: OrganizationDto;
}
export const ActiveScheduleDialog = ({
  handleEnableToggle,
  organization,
}: ActiveScheduleDialogProps) => {
  const dispatch = useDispatch();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const dialogProps = useSelector(getDialog("activeSchedule"));
  const handleDialogClose = () => {
    dispatch(
      setDialog({ dialog: "activeSchedule", open: false, props: undefined })
    );
  };

  const startAtSchedule = useMemo(() => {
    if (dialogProps?.props?.schedules) {
      return dialogProps.props.schedules.find(
        (schedule: ScheduleDto) => schedule.action === "start"
      )?.executionTime;
    } else return null;
  }, [dialogProps?.props?.schedules]);

  const pauseAtSchedule = useMemo(() => {
    if (dialogProps?.props?.schedules) {
      return dialogProps.props.schedules.find(
        (schedule: ScheduleDto) => schedule.action === "pause"
      )?.executionTime;
    } else return null;
  }, [dialogProps?.props?.schedules]);

  return (
    <IgDialog
      showDialog={dialogProps.isOpen}
      dialogContentWidth={"450px"}
      dialogHeight={null}
      maxWidth={"lg"}
      title={`This Experience is Already Scheduled`}
      handleClose={handleDialogClose}
      rawContent={
        <Grid
          container
          direction={"column"}
          rowSpacing={2}
          sx={{ textAlign: "left" }}
        >
          <Grid item>
            <IgTypography variant={"h3"}>
              This personalization is on the following schedule:
            </IgTypography>
          </Grid>
          <Grid
            item
            container
            direction={"column"}
            rowSpacing={1}
            sx={{ marginBottom: "16px" }}
          >
            {startAtSchedule && (
              <Grid item container direction={"row"} columnSpacing={4}>
                <Grid item>
                  <IgTypography variant={"h3"} sx={{ fontWeight: 600 }}>
                    Start
                  </IgTypography>
                </Grid>
                <Grid item>
                  <IgTypography variant={"h3"} sx={{ fontWeight: 600 }}>
                    {fullDateTimeConverter(
                      startAtSchedule,
                      organization.timezone ?? undefined,
                      storeCurrencyConfig?.value?.locale
                    )}
                  </IgTypography>
                </Grid>
              </Grid>
            )}
            {pauseAtSchedule && (
              <Grid
                item
                container
                direction={"row"}
                columnSpacing={4}
                sx={{ marginBottom: "8px" }}
              >
                <Grid item>
                  <IgTypography variant={"h3"} sx={{ fontWeight: 600 }}>
                    Stop
                  </IgTypography>
                </Grid>
                <Grid item>
                  <IgTypography variant={"h3"} sx={{ fontWeight: 600 }}>
                    {fullDateTimeConverter(
                      pauseAtSchedule,
                      organization.timezone ?? undefined,
                      storeCurrencyConfig?.value?.locale
                    )}
                  </IgTypography>
                </Grid>
              </Grid>
            )}
            <Grid item>
              <IgTypography variant={"h3"}>
                {startAtSchedule && pauseAtSchedule
                  ? "Activating this Experience will remove the scheduled start date. The scheduled stop date will remain as is."
                  : startAtSchedule
                    ? "This will remove the scheduled start date."
                    : "This will remove the scheduled stop date."}
              </IgTypography>
            </Grid>
          </Grid>
          <Grid item container justifyContent={"flex-end"} columnSpacing={1}>
            <Grid item>
              <IgButton onClick={handleDialogClose} size={"small"}>
                Cancel
              </IgButton>
            </Grid>
            <Grid item>
              <IgButton
                onClick={() => {
                  if (dialogProps.props?.experience) {
                    handleEnableToggle(dialogProps.props.experience);
                  }
                  handleDialogClose();
                }}
                size={"small"}
                variant={"contained"}
              >
                Confirm
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      }
      hideActions={true}
    />
  );
};
